/**
 * WebGL-рендерер интерактивной гиперболической карты.
 *
 * Меш — треугольники текущего уровня детализации (LOD); вершины заданы в
 * **координатах поверхности** (точки модели Пуанкаре / диска до применения вида).
 * Вершинный шейдер применяет текущий вид `V` как действие матрицы SU(1,1)
 * `z ↦ (α·z + β) / (β̄·z + ᾱ)` в комплексной арифметике и проецирует единичный
 * диск в клип-координаты с поправкой на соотношение сторон. Фрагментный шейдер
 * заливает поверхность фоновым цветом карты (Требование 4.7), поверх которого
 * позднее рисуются примитивы и метки.
 *
 * Граница диска и границы фундаментальной области рисуются отдельным проходом
 * линиями/дугами (Требование 4.6): стороны многоугольника тесселируются в
 * геодезические дуги модели Пуанкаре и отображаются тем же шейдерным конвейером.
 * При отсутствии WebGL показывается понятное сообщение (обработка ошибок,
 * см. {@link renderWebGLUnavailableMessage} и {@link WebGLMapRenderer.isWebGLAvailable}).
 *
 * Требования: 4.6, 3.1, 1.8, 4.7
 */

import type { Complex, Geometry, GeometryType, Isometry, Mobius, FundamentalPolygon } from '@langton/shared';
import { identity, asIsometry, createGeometry, hyperbolicGeometry } from '@langton/shared';
import type { Triangle } from '@langton/shared';

/**
 * Число отрезков тесселяции каждого ребра отладочного треугольника в
 * геодезическую дугу метрики (Требование 13): чем больше, тем плавнее видно
 * искривление сторон. `1` дало бы прямые хорды (как раньше).
 */
const DEBUG_TRIANGLE_EDGE_SEGMENTS = 6;

/** Числовой код геометрии для uniform `uGeometry` вершинного шейдера. */
function geometryCode(type: GeometryType): number {
  switch (type) {
    case 'euclidean':
      return 1;
    case 'spherical':
      return 2;
    case 'hyperbolic':
    default:
      return 0;
  }
}

/**
 * Вершинный шейдер: действие вида `V` на точку поверхности + проекция в клип.
 *
 * Действие зависит от геометрии карты (`uGeometry`): гиперболическая SU(1,1)
 * (`0`), евклидова аффинная (`1`) или сферическая SU(2) со стереографической
 * координатой и ортографической проекцией (`2`). Для антиголоморфного вида
 * (`uReversing = 1`, неориентируемая поверхность после пересечения обращающей
 * ориентацию склейки) аргумент сначала сопрягается (`z ↦ z̄`), что реализует
 * отражённое (зеркальное) продолжение поверхности (Требование 8).
 */
const VERTEX_SHADER_SOURCE = `
precision highp float;

attribute vec2 aSurface;      // точка поверхности z (координаты модели)

uniform vec2 uAlpha;          // α линейной части вида V (re, im)
uniform vec2 uBeta;           // β линейной части вида V (re, im)
uniform float uScale;         // масштаб отображения в клип-координаты
uniform vec2 uResolution;     // размер холста в пикселях (поправка соотношения сторон)
uniform int uGeometry;        // 0 = гиперболическая, 1 = евклидова, 2 = сферическая
uniform float uReversing;     // 1.0, если вид антиголоморфен (сопряжение аргумента)

// Комплексное умножение (a·b).
vec2 cmul(vec2 a, vec2 b) {
  return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
}

// Комплексное сопряжение (ā).
vec2 cconj(vec2 a) {
  return vec2(a.x, -a.y);
}

// Комплексное деление (a / b).
vec2 cdiv(vec2 a, vec2 b) {
  float d = b.x * b.x + b.y * b.y;
  return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / d;
}

void main() {
  // Антиголоморфный вид: сначала сопрягаем аргумент (z ↦ z̄).
  vec2 z = aSurface;
  if (uReversing > 0.5) {
    z = cconj(z);
  }

  vec2 w;
  if (uGeometry == 1) {
    // Евклидово аффинное движение: w = α·z + β.
    w = cmul(uAlpha, z) + uBeta;
  } else if (uGeometry == 2) {
    // Сферическая SU(2): w = (α·z + β) / (−β̄·z + ᾱ) — стереографическая координата,
    // затем ортографическая проекция сферы на экваториальный диск (x, y).
    vec2 snum = cmul(uAlpha, z) + uBeta;
    vec2 sden = cmul(-cconj(uBeta), z) + cconj(uAlpha);
    vec2 stereo = cdiv(snum, sden);
    float s = stereo.x * stereo.x + stereo.y * stereo.y;
    float d = 1.0 + s;
    w = vec2((2.0 * stereo.x) / d, (2.0 * stereo.y) / d);
  } else {
    // Гиперболическая SU(1,1): w = (α·z + β) / (β̄·z + ᾱ).
    vec2 num = cmul(uAlpha, z) + uBeta;
    vec2 den = cmul(cconj(uBeta), z) + cconj(uAlpha);
    w = cdiv(num, den);
  }

  // Проекция в клип-координаты с сохранением пропорций (диск остаётся кругом).
  vec2 clip = w * uScale;
  float aspect = uResolution.x / uResolution.y;
  if (aspect >= 1.0) {
    clip.x /= aspect;
  } else {
    clip.y *= aspect;
  }

  gl_Position = vec4(clip, 0.0, 1.0);
}
`;

/** Фрагментный шейдер: сплошная заливка выбранным цветом. */
const FRAGMENT_SHADER_SOURCE = `
precision mediump float;

uniform vec4 uColor;

void main() {
  gl_FragColor = uColor;
}
`;

/** Цвет в формате RGBA с компонентами в диапазоне `[0, 1]`. */
export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Режим отрисовки набора вершин для {@link WebGLMapRenderer.drawVertices}.
 * Абстрагирует константы WebGL, чтобы вызывающая сторона (например, слой
 * рисования) не зависела от контекста GL.
 */
export type DrawMode =
  | 'lines'
  | 'line-strip'
  | 'line-loop'
  | 'triangles'
  | 'triangle-fan'
  | 'triangle-strip';

/** Опции инициализации {@link WebGLMapRenderer}. */
export interface WebGLMapRendererOptions {
  /** Начальный цвет фона карты (hex, например `'#000000'` или `'#00000080'`). */
  backgroundColor?: string;
  /**
   * Масштаб отображения диска: во сколько единиц клип-координат укладывается
   * единичный радиус диска. `1` вписывает весь диск в видимую область.
   */
  scale?: number;
  /** Настройки границы диска (Требование 4.6). */
  diskBoundary?: DiskBoundaryOptions;
  /** Настройки границ фундаментальной области (Требование 4.6). */
  fundamentalDomain?: FundamentalDomainOptions;
}

/** Настройки отрисовки границы диска. */
export interface DiskBoundaryOptions {
  /** Рисовать ли границу диска (по умолчанию `true`). */
  enabled?: boolean;
  /** Цвет линии границы (hex, по умолчанию `'#ffffff'`). */
  color?: string;
  /** Число сегментов ломаной окружности (по умолчанию `128`). */
  segments?: number;
}

/** Настройки отрисовки границ фундаментальной области. */
export interface FundamentalDomainOptions {
  /** Рисовать ли границы фундаментальной области (по умолчанию `false`, Требование 3.1). */
  enabled?: boolean;
  /** Цвет линий границ (hex, по умолчанию `'#4da3ff'`). */
  color?: string;
  /** Число отрезков ломаной на каждую сторону (по умолчанию `24`). */
  segmentsPerSide?: number;
}

/**
 * Разобрать hex-цвет (`#RGB`, `#RRGGBB` или `#RRGGBBAA`) в {@link RgbaColor}
 * с компонентами `[0, 1]`. Некорректный ввод даёт непрозрачный чёрный.
 */
export function parseHexColor(hex: string): RgbaColor {
  const fallback: RgbaColor = { r: 0, g: 0, b: 0, a: 1 };
  if (typeof hex !== 'string') {
    return fallback;
  }
  let value = hex.trim();
  if (value.startsWith('#')) {
    value = value.slice(1);
  }
  // Короткая форма #RGB → #RRGGBB.
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (value.length !== 6 && value.length !== 8) {
    return fallback;
  }
  const num = Number.parseInt(value, 16);
  if (Number.isNaN(num)) {
    return fallback;
  }
  if (value.length === 6) {
    return {
      r: ((num >> 16) & 0xff) / 255,
      g: ((num >> 8) & 0xff) / 255,
      b: (num & 0xff) / 255,
      a: 1,
    };
  }
  return {
    r: ((num >>> 24) & 0xff) / 255,
    g: ((num >> 16) & 0xff) / 255,
    b: ((num >> 8) & 0xff) / 255,
    a: (num & 0xff) / 255,
  };
}

/**
 * Построить плоский буфер вершин и индексов из списка треугольников поверхности.
 * Вершины дедуплицируются по координатам (с округлением) для компактного меша.
 *
 * @param triangles треугольники LOD (вершины в координатах диска)
 * @returns вершины (`Float32Array` пар `x, y`) и индексы (`Uint32Array`)
 */
export function buildMeshFromTriangles(triangles: Triangle[]): {
  vertices: Float32Array;
  indices: Uint32Array;
} {
  const vertexList: number[] = [];
  const indexList: number[] = [];
  const lookup = new Map<string, number>();

  const keyOf = (p: Complex): string =>
    `${p.re.toFixed(9)},${p.im.toFixed(9)}`;

  const indexOf = (p: Complex): number => {
    const key = keyOf(p);
    const existing = lookup.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const index = vertexList.length / 2;
    vertexList.push(p.re, p.im);
    lookup.set(key, index);
    return index;
  };

  for (const [v0, v1, v2] of triangles) {
    indexList.push(indexOf(v0), indexOf(v1), indexOf(v2));
  }

  return {
    vertices: new Float32Array(vertexList),
    indices: new Uint32Array(indexList),
  };
}

/**
 * Построить вершины окружности радиуса `radius` в координатах диска для
 * отрисовки границы диска (Требование 4.6). Возвращает плоский `Float32Array`
 * пар `x, y`, рассчитанный на отрисовку в режиме `LINE_LOOP` (замкнутая ломаная).
 *
 * По умолчанию радиус равен `1` — идеальная граница модели Пуанкаре. Граница
 * инвариантна относительно вида `V` (изометрии диска сохраняют единичную
 * окружность), поэтому её достаточно нарисовать без применения вида.
 *
 * @param radius евклидов радиус окружности (по умолчанию `1`)
 * @param segments число сегментов ломаной (`segments ≥ 3`, по умолчанию `128`)
 */
export function buildCircleVertices(radius = 1, segments = 128): Float32Array {
  const count = Math.max(3, Math.floor(segments));
  const flat = new Float32Array(count * 2);
  for (let i = 0; i < count; i++) {
    const theta = (2 * Math.PI * i) / count;
    flat[i * 2] = radius * Math.cos(theta);
    flat[i * 2 + 1] = radius * Math.sin(theta);
  }
  return flat;
}

/**
 * Построить вершины границ фундаментальной области как геодезические дуги
 * между соседними вершинами многоугольника (Требование 4.6).
 *
 * Каждая сторона `[v_k, v_{k+1}]` тесселируется в ломаную из `segmentsPerSide`
 * отрезков вдоль геодезической дуги модели Пуанкаре (см. `geodesicArc` из
 * `@langton/shared`). Результат — плоский `Float32Array` пар `x, y`,
 * упорядоченный парами точек (отрезками) для отрисовки в режиме `LINES`.
 * Вид `V` применяется к этим точкам в вершинном шейдере тем же конвейером,
 * что и к мешу.
 *
 * @param vertices вершины многоугольника в координатах диска (в порядке обхода, `≥ 2`)
 * @param segmentsPerSide число отрезков ломаной на каждую сторону (`≥ 1`, по умолчанию `24`)
 */
export function buildGeodesicBorderVertices(
  vertices: Complex[],
  segmentsPerSide = 24,
  geometry: Geometry = hyperbolicGeometry,
): Float32Array {
  const n = vertices.length;
  if (n < 2) {
    return new Float32Array(0);
  }
  const segments = Math.max(1, Math.floor(segmentsPerSide));
  const flat: number[] = [];
  for (let k = 0; k < n; k++) {
    const a = vertices[k];
    const b = vertices[(k + 1) % n];
    // Тесселяция стороны в дугу в метрике геометрии: гиперболическая дуга
    // (модель Пуанкаре), евклидов отрезок (прямая) или сферическая дуга (slerp).
    // Для евклидовых вершин на |z| = 1 гиперболический путь бросал исключение
    // (`translateToOrigin`), поэтому геометрия обязательна (Требования 6, 8).
    const arc = geometry.geodesicArc(a, b, segments);
    // Каждый соседний отрезок дуги — независимая пара точек для режима LINES.
    for (let i = 0; i < arc.length - 1; i++) {
      const p = arc[i];
      const q = arc[i + 1];
      flat.push(p.re, p.im, q.re, q.im);
    }
  }
  return new Float32Array(flat);
}

/**
 * Построить плоский буфер вершин рёбер треугольников для отладочной прорисовки
 * триангуляции (Требование 13). Каждый треугольник `[v0, v1, v2]` даёт три
 * ребра-отрезка (`v0–v1`, `v1–v2`, `v2–v0`), то есть `3·T` отрезков = `6·T`
 * вершин из `T` треугольников, упорядоченных парами точек для режима `gl.LINES`.
 *
 * Вершины заданы в координатах поверхности (диска): тот же вид `V`, что и меш,
 * применяется к ним в вершинном шейдере, поэтому отладочные рёбра точно
 * совпадают с отрисованным мешем во всех видимых копиях (Требование 13.2).
 * Функция чистая: результат зависит только от входных треугольников, что
 * позволяет юнит-тестировать построитель буфера отдельно от контекста GL.
 *
 * @param triangles треугольники кадра (вершины в координатах диска)
 * @returns плоский `Float32Array` пар `x, y` длиной `12·T` (`6·T` вершин)
 */
export function buildTriangleEdgeVertices(
  triangles: Triangle[],
  geometry?: Geometry,
  segmentsPerEdge = 1,
): Float32Array {
  const seg = Math.max(1, Math.floor(segmentsPerEdge));

  // Быстрый путь и обратная совместимость: прямые рёбра (по одному отрезку на
  // ребро) — `3·T` отрезков = `12·T` чисел. Используется, если геометрия не
  // задана или тесселяция не требуется (`seg = 1`).
  if (!geometry || seg === 1) {
    const flat = new Float32Array(triangles.length * 12);
    let offset = 0;
    for (const [v0, v1, v2] of triangles) {
      flat[offset++] = v0.re;
      flat[offset++] = v0.im;
      flat[offset++] = v1.re;
      flat[offset++] = v1.im;
      flat[offset++] = v1.re;
      flat[offset++] = v1.im;
      flat[offset++] = v2.re;
      flat[offset++] = v2.im;
      flat[offset++] = v2.re;
      flat[offset++] = v2.im;
      flat[offset++] = v0.re;
      flat[offset++] = v0.im;
    }
    return flat;
  }

  // Геодезические рёбра (Требование 13): каждое ребро тесселируется в дугу
  // метрики геометрии (`seg` отрезков) и разбивается на пары точек для режима
  // `gl.LINES`. Так отладочные треугольники показывают искривление сторон в
  // соответствии с кривизной (гиперболические дуги, евклидовы прямые, сферические
  // дуги), а не хорды между вершинами. Вершины — в координатах поверхности; тот
  // же вид `V` применяется в шейдере, поэтому дуги совпадают с искривлением меша.
  const flat: number[] = [];
  const pushArc = (a: Complex, b: Complex): void => {
    const pts = geometry.geodesicArc(a, b, seg);
    for (let i = 0; i < pts.length - 1; i++) {
      flat.push(pts[i].re, pts[i].im, pts[i + 1].re, pts[i + 1].im);
    }
  };
  for (const [v0, v1, v2] of triangles) {
    pushArc(v0, v1);
    pushArc(v1, v2);
    pushArc(v2, v0);
  }
  return new Float32Array(flat);
}

/**
 * Отобразить в контейнере читаемое сообщение об отсутствии поддержки WebGL
 * (обработка ошибок для страницы карты). Существующее содержимое контейнера
 * очищается, вставляется блок с сообщением.
 *
 * Функция безопасна в не-браузерном окружении: при отсутствии `document`
 * она ничего не делает.
 *
 * @param container DOM-элемент, в который выводится сообщение
 * @param message текст сообщения (по умолчанию — стандартное объяснение)
 */
export function renderWebGLUnavailableMessage(
  container: HTMLElement,
  message = 'WebGL недоступен в этом браузере. Карта не может быть отображена. ' +
    'Обновите браузер или включите аппаратное ускорение и повторите попытку.',
): void {
  if (typeof document === 'undefined' || !container) {
    return;
  }
  container.textContent = '';
  const box = document.createElement('div');
  box.className = 'map-webgl-unavailable';
  box.setAttribute('role', 'alert');
  box.textContent = message;
  container.appendChild(box);
}

/**
 * Рендерер меша карты на WebGL.
 *
 * Жизненный цикл:
 * 1. `new WebGLMapRenderer(canvas, opts)` — создаёт контекст и компилирует шейдеры.
 * 2. `setMesh(...)` — загружает геометрию LOD.
 * 3. `setView(...)`, `setBackgroundColor(...)`, `setScale(...)`, `resize(...)` — настройка.
 * 4. `render()` — очистка фоном и отрисовка меша.
 * 5. `dispose()` — освобождение ресурсов GL.
 */
export class WebGLMapRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly gl: WebGLRenderingContext;
  private readonly program: WebGLProgram;

  // Локации атрибутов/униформ.
  private readonly aSurface: number;
  private readonly uAlpha: WebGLUniformLocation | null;
  private readonly uBeta: WebGLUniformLocation | null;
  private readonly uScale: WebGLUniformLocation | null;
  private readonly uResolution: WebGLUniformLocation | null;
  private readonly uColor: WebGLUniformLocation | null;
  private readonly uGeometry: WebGLUniformLocation | null;
  private readonly uReversing: WebGLUniformLocation | null;

  // Буферы геометрии.
  private readonly vertexBuffer: WebGLBuffer;
  private readonly indexBuffer: WebGLBuffer;
  private indexCount = 0;
  /** Использовать ли 32-битные индексы (расширение OES_element_index_uint). */
  private useUint32Indices = false;

  // Буферы и состояние линейных проходов (граница диска и фундаментальной области).
  private readonly boundaryBuffer: WebGLBuffer;
  private boundaryVertexCount = 0;
  private boundaryEnabled: boolean;
  private boundaryColor: RgbaColor;
  private boundarySegments: number;

  private readonly borderBuffer: WebGLBuffer;
  private borderVertexCount = 0;
  private borderEnabled: boolean;
  private borderColor: RgbaColor;
  private borderSegmentsPerSide: number;

  // Буфер и состояние отладочного прохода рёбер треугольников (Требование 13).
  private readonly triangleDebugBuffer: WebGLBuffer;
  private triangleDebugVertexCount = 0;
  private triangleDebugEnabled = false;
  private triangleDebugColor: RgbaColor = parseHexColor('#ff0000');

  // Динамический буфер для наложения произвольных вершин (слой рисования и т.п.).
  private readonly overlayBuffer: WebGLBuffer;

  // Состояние отрисовки.
  private view: Mobius = identity();
  /**
   * Антиголоморфность текущего вида `V` (Требование 8): `true`, если вид получен
   * пересечением обращающей ориентацию склейки на неориентируемой поверхности.
   * В этом случае вершинный шейдер сопрягает аргумент (`z ↦ z̄`), давая
   * зеркальное продолжение.
   */
  private viewReversing = false;
  /** Числовой код геометрии карты для шейдера (0 гипербола, 1 евклид, 2 сфера). */
  private geometryCodeValue = 0;
  /**
   * Реализация геометрии карты: используется для тесселяции границ
   * фундаментальной области в правильной метрике (Требования 6, 8). По умолчанию
   * гиперболическая; задаётся {@link setGeometry}.
   */
  private geometry: Geometry = hyperbolicGeometry;
  private backgroundColor: RgbaColor;
  /**
   * Исходный hex фонового цвета карты. Хранится отдельно от разобранного
   * {@link RgbaColor}, чтобы вернуть его без потерь потребителям — прежде всего
   * слою рисования, где маска стирания (Требование 1) смешивается с фоновым
   * цветом карты вдоль геометрии ластика.
   */
  private backgroundColorHex: string;
  private scale: number;

  /**
   * Проверить доступность WebGL в текущем окружении без создания рендерера.
   * Используется вызывающей стороной (задача 12.2) для показа сообщения при
   * отсутствии поддержки.
   */
  static isWebGLAvailable(): boolean {
    try {
      const probe = document.createElement('canvas');
      const ctx =
        probe.getContext('webgl') ??
        probe.getContext('experimental-webgl');
      return ctx !== null;
    } catch {
      return false;
    }
  }

  constructor(canvas: HTMLCanvasElement, options: WebGLMapRendererOptions = {}) {
    this.canvas = canvas;

    const gl =
      (canvas.getContext('webgl', { antialias: true }) as WebGLRenderingContext | null) ??
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) {
      throw new Error('WebGL is not available in this environment');
    }
    this.gl = gl;

    // 32-битные индексы позволяют мешу превышать 65 536 вершин на высоких LOD.
    this.useUint32Indices = gl.getExtension('OES_element_index_uint') !== null;

    this.program = this.createProgram(VERTEX_SHADER_SOURCE, FRAGMENT_SHADER_SOURCE);

    this.aSurface = gl.getAttribLocation(this.program, 'aSurface');
    this.uAlpha = gl.getUniformLocation(this.program, 'uAlpha');
    this.uBeta = gl.getUniformLocation(this.program, 'uBeta');
    this.uScale = gl.getUniformLocation(this.program, 'uScale');
    this.uResolution = gl.getUniformLocation(this.program, 'uResolution');
    this.uColor = gl.getUniformLocation(this.program, 'uColor');
    this.uGeometry = gl.getUniformLocation(this.program, 'uGeometry');
    this.uReversing = gl.getUniformLocation(this.program, 'uReversing');

    const vertexBuffer = gl.createBuffer();
    const indexBuffer = gl.createBuffer();
    const boundaryBuffer = gl.createBuffer();
    const borderBuffer = gl.createBuffer();
    const triangleDebugBuffer = gl.createBuffer();
    const overlayBuffer = gl.createBuffer();
    if (
      !vertexBuffer ||
      !indexBuffer ||
      !boundaryBuffer ||
      !borderBuffer ||
      !triangleDebugBuffer ||
      !overlayBuffer
    ) {
      throw new Error('Failed to allocate WebGL buffers');
    }
    this.vertexBuffer = vertexBuffer;
    this.indexBuffer = indexBuffer;
    this.boundaryBuffer = boundaryBuffer;
    this.borderBuffer = borderBuffer;
    this.triangleDebugBuffer = triangleDebugBuffer;
    this.overlayBuffer = overlayBuffer;

    this.backgroundColorHex = options.backgroundColor ?? '#000000';
    this.backgroundColor = parseHexColor(this.backgroundColorHex);
    this.scale = options.scale ?? 1;

    // Граница диска (Требование 4.6): по умолчанию видима, белая.
    const boundary = options.diskBoundary ?? {};
    this.boundaryEnabled = boundary.enabled ?? true;
    this.boundaryColor = parseHexColor(boundary.color ?? '#ffffff');
    this.boundarySegments = boundary.segments ?? 128;

    // Границы фундаментальной области: по умолчанию выключены (Требование 3.1, 3.2);
    // включаются только через отладочный тумблер администратора. Граница диска
    // не затрагивается; границы рисуются отдельным проходом `renderBorders`.
    const domain = options.fundamentalDomain ?? {};
    this.borderEnabled = domain.enabled ?? false;
    this.borderColor = parseHexColor(domain.color ?? '#4da3ff');
    this.borderSegmentsPerSide = domain.segmentsPerSide ?? 24;

    // Предзаполнить буфер границы диска (инвариантен относительно вида).
    this.updateBoundaryBuffer();

    this.resize(canvas.width || 1, canvas.height || 1);
  }

  /**
   * Установить текущий вид `V`. Принимает как матрицу {@link Mobius} (обратная
   * совместимость — сохраняющий ориентацию вид), так и обобщённую
   * {@link Isometry} (матрица + признак обращения ориентации); во втором случае
   * вершинный шейдер сопрягает аргумент для антиголоморфного (зеркального) вида
   * (Требование 8).
   */
  setView(view: Mobius | Isometry): void {
    const iso = asIsometry(view);
    this.view = iso.matrix;
    this.viewReversing = iso.orientationReversing;
  }

  /** Текущий вид `V` (матрица линейной части). */
  getView(): Mobius {
    return this.view;
  }

  /**
   * Задать тип геометрии карты (Требования 6, 8): выбирает действие вершинного
   * шейдера (гиперболическое SU(1,1), евклидово аффинное или сферическое SU(2)
   * с ортографической проекцией). По умолчанию гиперболическая.
   */
  setGeometry(type: GeometryType): void {
    this.geometryCodeValue = geometryCode(type);
    this.geometry = createGeometry(type);
  }

  /**
   * Установить масштаб отображения диска: во сколько единиц клип-координат
   * укладывается единичный радиус (`1` вписывает весь диск в видимую область).
   */
  setScale(scale: number): void {
    if (Number.isFinite(scale) && scale > 0) {
      this.scale = scale;
    }
  }

  /** Текущий масштаб отображения диска. */
  getScale(): number {
    return this.scale;
  }

  /** Установить фоновый цвет карты (hex). */
  setBackgroundColor(hex: string): void {
    this.backgroundColorHex = hex;
    this.backgroundColor = parseHexColor(hex);
  }

  /**
   * Текущий фоновый цвет карты в исходном hex-представлении. Используется слоем
   * рисования: маска стирания (Требование 1) заливается фоновым цветом карты,
   * визуально удаляя нижележащий контент; при отсутствии контента под маской
   * фон поверх фона не меняет изображение (Требование 1.8).
   */
  getBackgroundColorHex(): string {
    return this.backgroundColorHex;
  }

  /**
   * Настроить отрисовку границы диска (Требование 4.6): включение, цвет и
   * степень дискретизации окружности. Переданные поля переопределяют текущие.
   */
  setDiskBoundary(options: DiskBoundaryOptions = {}): void {
    if (options.enabled !== undefined) {
      this.boundaryEnabled = options.enabled;
    }
    if (options.color !== undefined) {
      this.boundaryColor = parseHexColor(options.color);
    }
    if (options.segments !== undefined && Number.isFinite(options.segments)) {
      this.boundarySegments = Math.max(3, Math.floor(options.segments));
      this.updateBoundaryBuffer();
    }
  }

  /**
   * Задать фундаментальный многоугольник, границы которого отрисовываются
   * геодезическими дугами (Требование 4.6). Стороны тесселируются и заносятся
   * в отдельный буфер линий; отрисовываются с применением текущего вида `V`.
   *
   * @param polygon фундаментальный многоугольник (используются его `vertices`)
   * @param options настройки отрисовки границ (включение, цвет, дискретизация)
   */
  setFundamentalDomain(
    polygon: FundamentalPolygon | null,
    options: FundamentalDomainOptions = {},
  ): void {
    if (options.enabled !== undefined) {
      this.borderEnabled = options.enabled;
    }
    if (options.color !== undefined) {
      this.borderColor = parseHexColor(options.color);
    }
    if (
      options.segmentsPerSide !== undefined &&
      Number.isFinite(options.segmentsPerSide)
    ) {
      this.borderSegmentsPerSide = Math.max(1, Math.floor(options.segmentsPerSide));
    }

    if (!polygon || polygon.vertices.length < 2) {
      this.borderVertexCount = 0;
      return;
    }

    const flat = buildGeodesicBorderVertices(
      polygon.vertices,
      this.borderSegmentsPerSide,
      this.geometry,
    );
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.borderBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, flat, gl.STATIC_DRAW);
    this.borderVertexCount = flat.length / 2;
  }

  /**
   * Настроить отладочную прорисовку рёбер треугольников триангуляции
   * (Требование 13): включение прохода и цвет линий. Переданные поля
   * переопределяют текущие; по образцу {@link setDiskBoundary} и
   * {@link setFundamentalDomain}. Проход управляется тем же отладочным
   * тумблером администратора, что и границы фундаментальной области, но
   * состоянием рендерер владеет независимо (Требования 13.1, 13.3, 13.4).
   *
   * @param options настройки отладочного прохода (включение, цвет; по умолчанию красный `#ff0000`)
   */
  setTriangleDebug(options: { enabled?: boolean; color?: string } = {}): void {
    if (options.enabled !== undefined) {
      this.triangleDebugEnabled = options.enabled;
    }
    if (options.color !== undefined) {
      this.triangleDebugColor = parseHexColor(options.color);
    }
  }

  /**
   * Задать набор треугольников кадра для отладочного прохода рёбер
   * (Требование 13). Каждый треугольник разбивается на три ребра-отрезка
   * (см. {@link buildTriangleEdgeVertices}) и заносится в отдельный буфер линий,
   * отрисовываемый в режиме `gl.LINES` с текущим видом `V`. Вызывается каждый
   * кадр теми же треугольниками, что и {@link setTriangleMesh}, поскольку набор
   * меняется с уровнем детализации, панорамированием и зумом (Требования 13.2,
   * 13.5).
   *
   * @param triangles треугольники кадра (вершины в координатах диска)
   */
  setDebugTriangleMesh(triangles: Triangle[]): void {
    // Рёбра тесселируются геодезическими дугами метрики карты, чтобы отладочные
    // треугольники показывали искривление сторон (а не прямые хорды) —
    // гиперболические/сферические дуги, евклидовы прямые (Требование 13).
    const flat = buildTriangleEdgeVertices(
      triangles,
      this.geometry,
      DEBUG_TRIANGLE_EDGE_SEGMENTS,
    );
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.triangleDebugBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, flat, gl.DYNAMIC_DRAW);
    this.triangleDebugVertexCount = flat.length / 2;
  }

  /** Перестроить буфер вершин границы диска по текущему числу сегментов. */
  private updateBoundaryBuffer(): void {
    const flat = buildCircleVertices(1, this.boundarySegments);
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.boundaryBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, flat, gl.STATIC_DRAW);
    this.boundaryVertexCount = flat.length / 2;
  }

  /**
   * Загрузить меш LOD.
   *
   * @param vertices вершины в координатах поверхности — плоский `Float32Array`
   *   пар `x, y` либо массив {@link Complex}
   * @param indices индексы треугольников (тройки вершин)
   */
  setMesh(
    vertices: Float32Array | Complex[],
    indices: Uint16Array | Uint32Array | number[],
  ): void {
    const gl = this.gl;

    const flat =
      vertices instanceof Float32Array
        ? vertices
        : flattenComplex(vertices);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, flat, gl.STATIC_DRAW);

    const indexArray = this.toIndexArray(indices);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexArray, gl.STATIC_DRAW);

    this.indexCount = indexArray.length;
  }

  /** Загрузить меш напрямую из треугольников LOD (см. {@link buildMeshFromTriangles}). */
  setTriangleMesh(triangles: Triangle[]): void {
    const { vertices, indices } = buildMeshFromTriangles(triangles);
    this.setMesh(vertices, indices);
  }

  /**
   * Синхронизировать размер буфера рисования с CSS-размером холста и обновить
   * вьюпорт WebGL. Возвращает `true`, если размер изменился.
   */
  resize(width: number, height: number): boolean {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.canvas.width === w && this.canvas.height === h) {
      return false;
    }
    this.canvas.width = w;
    this.canvas.height = h;
    this.gl.viewport(0, 0, w, h);
    return true;
  }

  /** Очистить холст фоновым цветом и отрисовать меш LOD с текущим видом. */
  private renderMesh(): void {
    const gl = this.gl;
    const { r, g, b, a } = this.backgroundColor;

    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(r, g, b, a);
    gl.clear(gl.COLOR_BUFFER_BIT);

    if (this.indexCount === 0) {
      return;
    }

    gl.useProgram(this.program);

    // Униформы вида и проекции.
    gl.uniform2f(this.uAlpha, this.view.alpha.re, this.view.alpha.im);
    gl.uniform2f(this.uBeta, this.view.beta.re, this.view.beta.im);
    gl.uniform1f(this.uScale, this.scale);
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);
    gl.uniform1i(this.uGeometry, this.geometryCodeValue);
    gl.uniform1f(this.uReversing, this.viewReversing ? 1 : 0);

    // Заливка поверхности фоновым цветом карты (Требование 4.7).
    gl.uniform4f(this.uColor, r, g, b, a);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.enableVertexAttribArray(this.aSurface);
    gl.vertexAttribPointer(this.aSurface, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    const indexType = this.useUint32Indices
      ? gl.UNSIGNED_INT
      : gl.UNSIGNED_SHORT;
    gl.drawElements(gl.TRIANGLES, this.indexCount, indexType, 0);
  }

  /**
   * Базовый проход кадра: фон, меш LOD и граница диска (Требования 4.6, 4.7).
   *
   * Отладочные наложения (границы фундаментальной области и — в будущем — рёбра
   * треугольников триангуляции) в этот проход НЕ входят: они вынесены в
   * {@link renderDebugOverlay} и рисуются вызывающей стороной отдельным шагом
   * ПОСЛЕ слоя рисования, чтобы маска стирания ластика не перекрывала
   * отладочные линии (Требования 10.3, 10.4).
   *
   * Меш и наложенные слои (см. {@link drawVertices}, используемый
   * `DrawingLayer`) проходят через **один и тот же** конвейер вершинного
   * шейдера: одно действие вида `V` (SU(1,1)) на точку поверхности и одна
   * проекция диска в клип-координаты с общими `uScale`/`uResolution`. Поэтому
   * для соседних копий, отрисованных под `V∘γ`, одинаковые координаты
   * поверхности `z` дают идентичные пиксели: рассогласование на общей границе
   * двух копий определяется только численной точностью изометрий склейки `γ`
   * (нормализованных SU(1,1)), что удерживает его существенно ниже 1 пикселя
   * при реалистичных масштабах (Требования 4.4, 4.6).
   */
  render(): void {
    this.renderMesh();
    this.renderDiskBoundary();
  }

  /**
   * Проход отладочных наложений поверх карты (Требование 10.4).
   *
   * Рисует границы фундаментальной области с применением текущего вида `V`
   * (и оставляет место для будущего прохода рёбер треугольников триангуляции —
   * задача 11.1). Вынесен из {@link render}, чтобы `MapPage.render` мог вызвать
   * его ОТДЕЛЬНЫМ ШАГОМ после слоя рисования: тогда непрозрачная маска стирания
   * ластика (Требование 10) не перекрывает отладочные линии (Требования 10.3,
   * 10.4). Использует тот же шейдерный конвейер (действие `V` + проекция диска),
   * что и меш, поэтому наложения точно совпадают с мешем по координатам.
   */
  renderDebugOverlay(): void {
    const gl = this.gl;
    const drawBorders = this.borderEnabled && this.borderVertexCount > 0;
    const drawTriangles =
      this.triangleDebugEnabled && this.triangleDebugVertexCount > 0;
    if (!drawBorders && !drawTriangles) {
      return;
    }
    gl.useProgram(this.program);
    gl.uniform1f(this.uScale, this.scale);
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);

    // Границы фундаментальной области — с текущим видом V.
    if (drawBorders) {
      this.drawLines(
        this.borderBuffer,
        this.borderVertexCount,
        this.borderColor,
        this.view,
        this.viewReversing,
        gl.LINES,
      );
    }

    // Отладочные рёбра треугольников триангуляции — тем же видом V, красным
    // цветом (Требования 13.1, 13.2, 13.5).
    if (drawTriangles) {
      this.drawLines(
        this.triangleDebugBuffer,
        this.triangleDebugVertexCount,
        this.triangleDebugColor,
        this.view,
        this.viewReversing,
        gl.LINES,
      );
    }
  }

  /**
   * Проход границы диска в составе базового {@link render}. Окружность
   * инвариантна относительно вида `V` (изометрии диска сохраняют единичную
   * окружность), поэтому рисуется с единичным видом. Это НЕ отладочное
   * наложение — граница диска остаётся частью базового кадра.
   */
  private renderDiskBoundary(): void {
    const gl = this.gl;
    if (!this.boundaryEnabled || this.boundaryVertexCount === 0) {
      return;
    }
    gl.useProgram(this.program);
    gl.uniform1f(this.uScale, this.scale);
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);

    // Граница диска инвариантна к виду и рисуется тождественным (сохраняющим
    // ориентацию) видом; при любой геометрии единичная окружность |z| = 1
    // проецируется в единичную окружность (для сферической — экватор).
    this.drawLines(
      this.boundaryBuffer,
      this.boundaryVertexCount,
      this.boundaryColor,
      identity(),
      false,
      gl.LINE_LOOP,
    );
  }

  /** Отрисовать содержимое буфера линий заданным цветом, видом и режимом. */
  private drawLines(
    buffer: WebGLBuffer,
    vertexCount: number,
    color: RgbaColor,
    view: Mobius,
    reversing: boolean,
    mode: number,
  ): void {
    const gl = this.gl;
    gl.uniform2f(this.uAlpha, view.alpha.re, view.alpha.im);
    gl.uniform2f(this.uBeta, view.beta.re, view.beta.im);
    gl.uniform1i(this.uGeometry, this.geometryCodeValue);
    gl.uniform1f(this.uReversing, reversing ? 1 : 0);
    gl.uniform4f(this.uColor, color.r, color.g, color.b, color.a);

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(this.aSurface);
    gl.vertexAttribPointer(this.aSurface, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(mode, 0, vertexCount);
  }

  /** Псевдоним {@link render}. */
  draw(): void {
    this.render();
  }

  /**
   * Отрисовать произвольный набор вершин (в координатах поверхности) заданным
   * цветом под указанным видом `V`, используя тот же шейдерный конвейер, что и
   * основной меш (действие SU(1,1) + проекция диска). Предназначен для наложения
   * слоёв поверх карты — прежде всего для рендера примитивов рисования
   * (см. `DrawingLayer`): линии/полилинии как геодезические аппроксимации,
   * треугольники как заливка.
   *
   * Вершины загружаются в динамический буфер и отрисовываются в выбранном режиме.
   * Метод не очищает холст и не трогает основной меш — вызывается после
   * {@link render} тем же кадром. Ширина линий в WebGL не гарантируется драйвером;
   * для «толстых» мазков вызывающая сторона должна тесселировать полосу в
   * треугольники и рисовать в режиме `'triangles'`.
   *
   * @param vertices вершины в координатах поверхности (плоский `Float32Array`
   *   пар `x, y` либо массив {@link Complex})
   * @param mode режим отрисовки ({@link DrawMode})
   * @param color цвет (hex-строка или {@link RgbaColor})
   * @param view вид `V`, применяемый к вершинам (по умолчанию текущий вид рендерера)
   */
  drawVertices(
    vertices: Float32Array | Complex[],
    mode: DrawMode,
    color: string | RgbaColor,
    view: Mobius | Isometry = { matrix: this.view, orientationReversing: this.viewReversing },
  ): void {
    const gl = this.gl;
    const iso = asIsometry(view);
    const flat =
      vertices instanceof Float32Array ? vertices : flattenComplex(vertices);
    if (flat.length < 2) {
      return;
    }
    // Устойчивость к вырожденным изометриям (Требование 4.7): если вид `V`
    // (например, композиция с изометрией склейки копии `V∘γ`) или какая-либо
    // вершина копии нечисловые (NaN/Infinity от вырожденной/почти-сингулярной
    // изометрии), пропускаем только эту отрисовку. Кадр не прерывается, уже
    // отрисованный контент и остальные копии сохраняются.
    if (!isFiniteView(iso.matrix) || !isFiniteFlatVertices(flat)) {
      return;
    }
    const rgba = typeof color === 'string' ? parseHexColor(color) : color;

    gl.useProgram(this.program);
    gl.uniform1f(this.uScale, this.scale);
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);
    gl.uniform1i(this.uGeometry, this.geometryCodeValue);
    gl.uniform1f(this.uReversing, iso.orientationReversing ? 1 : 0);
    gl.uniform2f(this.uAlpha, iso.matrix.alpha.re, iso.matrix.alpha.im);
    gl.uniform2f(this.uBeta, iso.matrix.beta.re, iso.matrix.beta.im);
    gl.uniform4f(this.uColor, rgba.r, rgba.g, rgba.b, rgba.a);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.overlayBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, flat, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(this.aSurface);
    gl.vertexAttribPointer(this.aSurface, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(this.glMode(mode), 0, flat.length / 2);
  }

  /** Сопоставить {@link DrawMode} с константой примитива WebGL. */
  private glMode(mode: DrawMode): number {
    const gl = this.gl;
    switch (mode) {
      case 'lines':
        return gl.LINES;
      case 'line-strip':
        return gl.LINE_STRIP;
      case 'line-loop':
        return gl.LINE_LOOP;
      case 'triangles':
        return gl.TRIANGLES;
      case 'triangle-fan':
        return gl.TRIANGLE_FAN;
      case 'triangle-strip':
        return gl.TRIANGLE_STRIP;
      default:
        return gl.LINE_STRIP;
    }
  }

  /** Освободить ресурсы WebGL (буферы, программу, шейдеры). */
  dispose(): void {
    const gl = this.gl;
    gl.deleteBuffer(this.vertexBuffer);
    gl.deleteBuffer(this.indexBuffer);
    gl.deleteBuffer(this.boundaryBuffer);
    gl.deleteBuffer(this.borderBuffer);
    gl.deleteBuffer(this.triangleDebugBuffer);
    gl.deleteBuffer(this.overlayBuffer);
    gl.deleteProgram(this.program);
    this.indexCount = 0;
    this.boundaryVertexCount = 0;
    this.borderVertexCount = 0;
    this.triangleDebugVertexCount = 0;
  }

  /** Подобрать тип массива индексов с учётом поддержки 32-битных индексов. */
  private toIndexArray(
    indices: Uint16Array | Uint32Array | number[],
  ): Uint16Array | Uint32Array {
    if (indices instanceof Uint16Array) {
      this.useUint32Indices = false;
      return indices;
    }
    if (indices instanceof Uint32Array) {
      if (!this.useUint32Indices) {
        return Uint16Array.from(indices);
      }
      return indices;
    }
    // Обычный массив чисел: выбрать разрядность по максимальному индексу.
    let max = 0;
    for (const i of indices) {
      if (i > max) {
        max = i;
      }
    }
    if (max > 0xffff && this.useUint32Indices) {
      return Uint32Array.from(indices);
    }
    this.useUint32Indices = false;
    return Uint16Array.from(indices);
  }

  /** Скомпилировать и слинковать шейдерную программу. */
  private createProgram(vertexSource: string, fragmentSource: string): WebGLProgram {
    const gl = this.gl;
    const vertexShader = this.compileShader(gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, fragmentSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error('Failed to create WebGL program');
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    // Шейдеры можно удалить после линковки — программа сохраняет их код.
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Failed to link WebGL program: ${log ?? 'unknown error'}`);
    }
    return program;
  }

  /** Скомпилировать отдельный шейдер и проверить статус компиляции. */
  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) {
      throw new Error('Failed to create WebGL shader');
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Failed to compile WebGL shader: ${log ?? 'unknown error'}`);
    }
    return shader;
  }
}

/**
 * Проверить, что вид `V` (матрица SU(1,1) `{ alpha, beta }`) состоит из конечных
 * компонент. Композиция с вырожденной изометрией склейки копии может дать
 * нечисловые `alpha`/`beta`; при этом соответствующую копию рендер пропускает
 * (Требование 4.7).
 */
function isFiniteView(view: Mobius | undefined | null): view is Mobius {
  return (
    !!view &&
    !!view.alpha &&
    !!view.beta &&
    Number.isFinite(view.alpha.re) &&
    Number.isFinite(view.alpha.im) &&
    Number.isFinite(view.beta.re) &&
    Number.isFinite(view.beta.im)
  );
}

/** Проверить, что все компоненты плоского буфера вершин конечны (без NaN/Infinity). */
function isFiniteFlatVertices(flat: Float32Array): boolean {
  for (let i = 0; i < flat.length; i++) {
    if (!Number.isFinite(flat[i])) {
      return false;
    }
  }
  return true;
}

/** Преобразовать массив {@link Complex} в плоский `Float32Array` пар `x, y`. */
function flattenComplex(points: Complex[]): Float32Array {
  const flat = new Float32Array(points.length * 2);
  for (let i = 0; i < points.length; i++) {
    flat[i * 2] = points[i].re;
    flat[i * 2 + 1] = points[i].im;
  }
  return flat;
}
